// Slack message content

// Software development team Slack messages

// Demo messages - sequence of user messages for the auto demo
export const demoMessages = [
  "Can you find the PR that fixed the authentication bug last week?",
  "There's a critical bug in the payment processing flow. Can you create a Jira ticket and assign it to the backend team?",
  "We need to schedule a post-mortem for the API outage that happened yesterday",
  "Can you show me all recent deployments to production?",
  "Please generate a summary report of all open bugs in the current sprint",
  "What was our total AWS spend on S3 last month?"
];

// Predefined tool responses for each demo message
export const demoResponses: Record<string, {tool: string, input: any, output: any}> = {
  "Can you find the PR that fixed the authentication bug last week?": {
    tool: 'github_search',
    input: { 
      query: "authentication bug fix", 
      timeframe: "last week",
      repos: ["main-app", "auth-service"]
    },
    output: { 
      results: [
        { 
          pr_number: "#387", 
          title: "Fix JWT token validation bug in auth middleware", 
          author: "alex.dev", 
          status: "merged",
          repo: "auth-service",
          merged_at: "Mar 28, 2024",
          comments: 5,
          changes: "+56 -12"
        },
        { 
          pr_number: "#392", 
          title: "Update auth error handling and logging", 
          author: "sarah.eng", 
          status: "merged",
          repo: "main-app",
          merged_at: "Mar 30, 2024",
          comments: 3,
          changes: "+27 -4"
        }
      ],
      branches: ["main", "develop"],
      total_matches: 4,
      search_id: "search_pr_123"
    }
  },
  "There's a critical bug in the payment processing flow. Can you create a Jira ticket and assign it to the backend team?": {
    tool: 'jira_create',
    input: { 
      summary: "Critical bug in payment processing flow", 
      description: "Users are experiencing failed payments despite being charged. Issue appears in the transaction confirmation step.",
      type: "Bug",
      priority: "Critical",
      assignee: "backend-team",
      labels: ["payment", "critical", "customer-impact"]
    },
    output: { 
      ticket_id: "PAY-1293",
      created: true,
      assignee: "backend-team",
      sprint: "Sprint 45",
      priority: "Critical",
      status: "To Do",
      link: "https://company.atlassian.net/browse/PAY-1293",
      watchers: ["product-manager", "frontend-team-lead", "cto"]
    }
  },
  "We need to schedule a post-mortem for the API outage that happened yesterday": {
    tool: 'meeting_schedule',
    input: { 
      title: "API Outage Post-mortem", 
      duration: "60m",
      required_attendees: ["engineering-leads", "sre-team", "product-manager"],
      optional_attendees: ["engineering-all"],
      suggested_times: ["tomorrow 10:00 AM", "tomorrow 2:00 PM"],
      location: "Zoom"
    },
    output: { 
      event_id: "evt_12345",
      created: true,
      meeting_link: "https://zoom.us/j/1234567890?pwd=abcDEF",
      confirmed_time: "Apr 18, 2024, 2:00 PM - 3:00 PM",
      calendar: "Engineering Calendar",
      attendees_count: 12,
      meeting_document: "https://docs.company.com/post-mortem-apr17-outage",
      incident_reference: "INC-4721"
    }
  },
  "Can you show me all recent deployments to production?": {
    tool: 'deployment_search',
    input: { 
      environment: "production", 
      count: 5,
      services: ["all"]
    },
    output: { 
      deployments: [
        { 
          service: "main-app", 
          version: "v3.8.2", 
          timestamp: "Apr 16, 2024, 15:34 UTC", 
          deployer: "ci-pipeline", 
          status: "successful",
          commit: "a72de3f",
          build_time: "12m 43s"
        },
        { 
          service: "auth-service", 
          version: "v2.5.0", 
          timestamp: "Apr 15, 2024, 11:09 UTC", 
          deployer: "ci-pipeline", 
          status: "successful",
          commit: "f83bd71",
          build_time: "8m 12s"
        },
        { 
          service: "payment-api", 
          version: "v1.9.5", 
          timestamp: "Apr 14, 2024, 09:22 UTC", 
          deployer: "manual-hotfix", 
          status: "successful",
          commit: "e74bd22",
          build_time: "7m 55s"
        },
        { 
          service: "notification-service", 
          version: "v2.2.1", 
          timestamp: "Apr 12, 2024, 14:47 UTC", 
          deployer: "ci-pipeline", 
          status: "successful",
          commit: "92ca4d5",
          build_time: "6m 28s"
        },
        { 
          service: "analytics-processor", 
          version: "v4.1.0", 
          timestamp: "Apr 10, 2024, 10:15 UTC", 
          deployer: "ci-pipeline", 
          status: "successful",
          commit: "b3d7f12",
          build_time: "15m 02s"
        }
      ],
      environment_status: "healthy",
      total_services: 8,
      search_id: "search_deploy_456"
    }
  },
  "Please generate a summary report of all open bugs in the current sprint": {
    tool: 'sprint_report',
    input: { 
      sprint: "current", 
      type: "bugs",
      status: "open"
    },
    output: { 
      sprint_name: "Sprint 45 (Apr 10 - Apr 24)",
      total_bugs: 14,
      critical: 1,
      high: 5,
      medium: 6,
      low: 2,
      by_component: [
        { name: "Frontend", count: 5 },
        { name: "Backend", count: 7 },
        { name: "Infrastructure", count: 2 }
      ],
      by_assignee: [
        { name: "frontend-team", count: 5 },
        { name: "backend-team", count: 6 },
        { name: "devops-team", count: 2 },
        { name: "unassigned", count: 1 }
      ],
      oldest_bug: "PAY-1278 (8 days old)",
      burndown_url: "https://company.atlassian.net/sprint-reports/45/burndown",
      report_id: "sprint_report_789"
    }
  },
  "What was our total AWS spend on S3 last month?": {
    tool: 'aws_cost_report',
    input: { 
      service: "S3", 
      timeframe: "last month",
      breakdown: "by bucket",
      limit: 5
    },
    output: { 
      total_cost: "$4,287.65",
      period: "March 1-31, 2024",
      currency: "USD",
      top_buckets: [
        { 
          name: "production-assets", 
          total_cost: "$1,342.78", 
          storage_cost: "$842.30",
          data_transfer_in: "$127.65",
          data_transfer_out: "$372.83",
          api_requests: "$0.00"
        },
        { 
          name: "analytics-data", 
          total_cost: "$987.54", 
          storage_cost: "$567.25",
          data_transfer_in: "$54.12",
          data_transfer_out: "$366.17",
          api_requests: "$0.00"
        },
        { 
          name: "user-uploads", 
          total_cost: "$745.32", 
          storage_cost: "$415.60",
          data_transfer_in: "$87.45",
          data_transfer_out: "$242.27",
          api_requests: "$0.00"
        },
        { 
          name: "backup-archive", 
          total_cost: "$632.10", 
          storage_cost: "$598.75",
          data_transfer_in: "$33.35",
          data_transfer_out: "$0.00",
          api_requests: "$0.00"
        },
        { 
          name: "static-website", 
          total_cost: "$579.91", 
          storage_cost: "$78.45",
          data_transfer_in: "$21.23",
          data_transfer_out: "$480.23",
          api_requests: "$0.00"
        }
      ],
      month_over_month_change: "+12.5%",
      report_id: "cost_report_s3_mar2024"
    }
  }
};

// Initial responses for each tool type
export const initialResponses: Record<string, string> = {
  github_search: "🔍 Finding that auth bug PR...",
  jira_create: "🐛 Creating ticket for payment bug...",
  meeting_schedule: "📅 Scheduling post-mortem...",
  deployment_search: "🚀 Checking recent deployments...",
  sprint_report: "📊 Generating bug report...",
  aws_cost_report: "💰 Fetching AWS S3 cost data..."
};

// Response templates for each tool type
export const responseTemplates: Record<string, string> = {
  github_search: `🔍 *Found 2 auth bug PRs:*

• \`PR #387\` *Fix JWT token validation bug in auth middleware*
  👤 alex.dev | 📆 Mar 28, 2024 | 📁 auth-service
  📝 +56 -12 | 🔗 <https://github.com/company/auth-service/pull/387|PR Link>

• \`PR #392\` *Update auth error handling and logging*
  👤 sarah.eng | 📆 Mar 30, 2024 | 📁 main-app
  📝 +27 -4 | 🔗 <https://github.com/company/main-app/pull/392|PR Link>

Need details on either PR?`,
  
  jira_create: `✅ *PAY-1293 created!*

🐛 *Critical payment processing bug*
🚨 Priority: Critical
👥 Assigned: backend-team
🏃‍♂️ Sprint: Sprint 45
🔗 <https://company.atlassian.net/browse/PAY-1293|Ticket Link>

Added watchers from product, frontend, and CTO office. Notify #backend?`,
  
  meeting_schedule: `📅 *Post-mortem scheduled!*

🕑 Apr 18, 2024, 2:00 PM - 3:00 PM
🔗 <https://zoom.us/j/1234567890?pwd=abcDEF|Meeting Link>
👥 12 team members invited
🔗 <https://docs.company.com/post-mortem-apr17-outage|Document Link>
🔗 <https://company.atlassian.net/browse/INC-4721|Incident Link>

Calendar invites sent to all participants.`,
  
  deployment_search: `🚀 *Latest Production Deployments:*

1️⃣ *main-app* (v3.8.2)
   📆 Apr 16, 2024, 15:34 UTC
   ✅ successful | 🔗 <https://github.com/company/main-app/commit/a72de3f|Commit Link>

2️⃣ *auth-service* (v2.5.0)
   📆 Apr 15, 2024, 11:09 UTC
   ✅ successful | 🔗 <https://github.com/company/auth-service/commit/f83bd71|Commit Link>

3️⃣ *payment-api* (v1.9.5)
   📆 Apr 14, 2024, 09:22 UTC
   ✅ successful | 🔗 <https://github.com/company/payment-api/commit/e74bd22|Commit Link>

🟢 Environment: healthy | 🔗 <https://deploy.company.com/status|Status Dashboard>`,
  
  sprint_report: `🐞 *Sprint 45 (Apr 10 - Apr 24) Bugs:*

📊 *14 open bugs*
🚨 1 critical | 🔴 5 high
🟠 6 medium | 🟡 2 low

📋 *By Component:*
💻 Frontend: 5
⚙️ Backend: 7
🏗️ Infrastructure: 2

⏳ Oldest: PAY-1278 (8 days old) | 🔗 <https://company.atlassian.net/browse/PAY-1278|Ticket Link>
🔗 <https://company.atlassian.net/sprint-reports/45/burndown|Burndown Chart>

Create detailed report or assign triage?`,
  
  aws_cost_report: `💰 *AWS S3 Costs (March 1-31, 2024):*

📊 *Total S3 Spend: $4,287.65* (+12.5% from previous month)

🔶 *Top 5 Buckets by Cost:*
1️⃣ *production-assets*: $1,342.78 (💾 $842.30 | ⬆️ $127.65 | ⬇️ $372.83)
2️⃣ *analytics-data*: $987.54 (💾 $567.25 | ⬆️ $54.12 | ⬇️ $366.17)
3️⃣ *user-uploads*: $745.32 (💾 $415.60 | ⬆️ $87.45 | ⬇️ $242.27)
4️⃣ *backup-archive*: $632.10 (💾 $598.75 | ⬆️ $33.35 | ⬇️ $0.00)
5️⃣ *static-website*: $579.91 (💾 $78.45 | ⬆️ $21.23 | ⬇️ $480.23)

🔗 <https://aws.amazon.com/console/billing/cost-report-s3-mar2024|View Full Report>

Would you like to see optimization recommendations?`
};
